import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import classNames from 'classnames'
import { omit } from 'lodash'
import { Column } from 'react-table'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { linkBuilder } from 'shared/linkBuilder'
import { getPagesCount } from '../utils'
import { IBaseOverviewFilter } from '../interfaces'
import {
  SqlOrderDirectionEnum,
  UserEventModel,
  UserEventsOverviewOrderColumn
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useUserEventsAnalyticsOverviewQuery } from './EventOverview.codegen'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'
import {
  StatusRSVPAccepted,
  StatusRSVPDeclined,
  StatusRSVPNoResponse
} from '../../manage-events/components/event-status/EventStatus'
import { Text } from 'front-end/src/components'

interface IProps {
  userId: number
}

interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: UserEventsOverviewOrderColumn
}

export const EventsOverviewTable: FC<IProps> = ({ userId }: IProps) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewFilter, setOverviewFilters] = useState<IOverviewFilter>({
    limit: 20,
    orderColumn: UserEventsOverviewOrderColumn.eventName,
    orderDirection: SqlOrderDirectionEnum.ASC
  })

  const overviewTableQuery = {
    ...overviewFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useUserEventsAnalyticsOverviewQuery({
    variables: {
      mainAnalyticsFilter: omit(filterAsQueryVariables, 'groupIds'),
      overviewFilter: overviewTableQuery,
      userId
    }
  })

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Event name`,
      id: EventOverviewOrderColumn.eventName,
      accessor: 'event.title',
      Cell: (row) => {
        return (
          <>
            <Text isTruncated fontWeight="bold" className="dark">
              {row.value}
            </Text>
            {row.original.event.deleted_at && <StatusArchivedGrey />}
          </>
        )
      }
    },
    {
      Header: t`Start time`,
      maxWidth: 170,
      accessor: 'event.start_time',
      id: EventOverviewOrderColumn.startTime,
      Cell: (row) => formatDateAndTimeShort(row.value)
    },
    {
      Header: t`End time`,
      maxWidth: 170,
      accessor: 'event.end_time',
      id: EventOverviewOrderColumn.endTime,
      Cell: (row) => formatDateAndTimeShort(row.value)
    },
    {
      Header: 'RSVP',
      accessor: 'attending',
      maxWidth: 140,
      Cell: (row) => {
        if (row.value === null) {
          return <StatusRSVPNoResponse />
        }
        return row.value ? <StatusRSVPAccepted /> : <StatusRSVPDeclined />
      }
    },
    {
      Header: t`Attended`,
      accessor: 'present',
      maxWidth: 100,
      Cell: (row) => {
        if (row.value === null) {
          return null
        }
        return (
          <span
            className={classNames('completion-status', {
              pass: row.value,
              fail: !row.value
            })}
          >
            {row.value ? <Trans>Yes</Trans> : <Trans>No</Trans>}
          </span>
        )
      }
    }
  ]

  let pagesCount = 1
  if (data && data.user) {
    pagesCount = getPagesCount(
      data.user?.analytics?.events?.userEventsOverviewCount as number,
      overviewTableQuery.limit
    )
  }

  return (
    <>
      <TableExtendedOld
        loading={loading}
        manual
        data={
          data?.user?.analytics?.events?.userEventsOverview as UserEventModel[]
        }
        tableHeader={t`Events`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={[EventOverviewOrderColumn.eventName]}
        getTrProps={(_state, row) => ({
          onClick: () =>
            navigateToAndResetPageAndSearch(
              linkBuilder.eventDetail(row.original.event.id),
              changeFilter
            ),
          className: `clickable ${
            row && row.original.event.deleted_at ? 'archived' : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as UserEventsOverviewOrderColumn)
                : UserEventsOverviewOrderColumn.eventName,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </>
  )
}

export enum EventOverviewOrderColumn {
  eventName = 'eventName',
  startTime = 'startTime',
  endTime = 'endTime',
  attending = 'attending'
}
